<template>
  <ion-page>
    <ion-content>
      <the-header
        :title="t('editProfile')"
        :to="backButtonRoutePath"
        :show-avatar="false"
      />
      <div class="edit-profile-page content-container">
        <div class="edit-profile-page__avatar">
          <img
            v-if="user?.avatarUrl"
            class="edit-profile-page__avatar-img"
            :src="getFirebaseStorageResizedAvatar(user.avatarUrl, 'profile')"
            alt=""
          />
          <template v-else> {{ avatarName }}</template>
          <button
            v-if="user?.avatarUrl"
            type="button"
            class="edit-profile-page__delete-avatar"
            @click="state.modalsVisibility.deletePhotoConfirmation = true"
          >
            <img src="/assets/icon/delete-avatar-icon.svg" alt="Delete" />
          </button>
        </div>
        <div class="edit-profile-page__username">{{ user.alias }}</div>
        <button
          v-for="(button, bK) in buttons"
          :key="bK"
          type="button"
          class="edit-profile-page__button"
          @click="button.handler()"
        >
          <span class="edit-profile-page__button-left">
            <span class="edit-profile-page__button-icon">
              <ion-icon v-if="button.icon" :icon="button.icon" />
              <img v-else :src="`/assets/icon/${button.image}`" alt="" />
            </span>
            <span>{{ button.name }}</span>
          </span>
          <span
            v-if="!button.isHideArrow"
            class="edit-profile-page__button-arrow"
          >
            <ion-icon :icon="chevronForwardOutline" />
          </span>
        </button>
      </div>
      <base-modal
        :title="t('modals.deletePhotoTitle')"
        :is-opened="state.modalsVisibility.deletePhotoConfirmation"
        class="confirm-modal"
        @close="state.modalsVisibility.deletePhotoConfirmation = false"
      >
        <base-button class="modal__btn" @click="deletePhoto">
          {{ t('yes') }}
        </base-button>
        <base-button
          white
          class="modal__btn"
          @click="state.modalsVisibility.deletePhotoConfirmation = false"
        >
          {{ t('no') }}
        </base-button>
      </base-modal>
      <base-modal
        :title="t('modals.logoutTitle')"
        :is-opened="state.modalsVisibility.logoutConfirmation"
        class="confirm-modal"
        @close="state.modalsVisibility.logoutConfirmation = false"
      >
        <base-button class="modal__btn" @click="logout">
          {{ t('yes') }}
        </base-button>
        <base-button
          white
          class="modal__btn"
          @click="state.modalsVisibility.logoutConfirmation = false"
        >
          {{ t('no') }}
        </base-button>
      </base-modal>
      <base-modal
        :title="t('modals.deleteAccountTitle')"
        :is-opened="state.modalsVisibility.deleteAccountConfirmation"
        class="delete-account-modal"
        @close="closeDeleteConfirmationModal"
      >
        <p class="modal-text">
          {{ t('modals.deleteAccountText') }}
        </p>
        <div class="modal-container">
          <div v-if="user.authMethod === 'password'" class="modal__input">
            <base-input
              v-model="state.password"
              type="password"
              placeholder="Password"
            />
          </div>
          <base-button
            @click="handleDeleteAccount"
            :loading="state.loadingMap.deleteAccount"
            class="modal__btn"
          >
            {{ t('confirm') }}
          </base-button>
          <base-button
            @click="closeDeleteConfirmationModal"
            secondary
            class="modal__btn"
            :disabled="state.loadingMap.deleteAccount"
          >
            {{ t('cancel') }}
          </base-button>
        </div>
      </base-modal>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { IonPage, IonContent, IonIcon } from '@ionic/vue';
import TheHeader from '@/components/TheHeader.vue';
import BaseModal from '@/components/modals/BaseModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseInput from '@/components/BaseInput.vue';
import { useUserStore } from '@/stores/user';
import {
  lockClosedOutline,
  mailOutline,
  chevronForwardOutline,
  personOutline,
  globeOutline,
} from 'ionicons/icons';
import { useRouter } from 'vue-router';
import api from '@/api';
import { logEvent } from '@/services/analytics';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { getFirebaseStorageResizedAvatar } from '@/services/methods';
import { useInternalization } from '@/composables/useInternalization';
import { SourceLang } from '@/types';

const userStore = useUserStore();
const user = computed(() => userStore.user);
const router = useRouter();
const { t, setLocale } = useInternalization();

const state = reactive({
  modalsVisibility: {
    deletePhotoConfirmation: false,
    logoutConfirmation: false,
    deleteAccountConfirmation: false,
    language: false,
  },
  password: '',
  loadingMap: {
    deleteAccount: false,
  },
});

interface menuButtonI {
  name: string;
  icon?: string;
  image?: string;
  handler: () => void;
  isHideArrow?: boolean;
}

const avatarName = computed(
  () => user.value?.alias[0] + user.value?.alias[1] || ''
);
const buttons = computed(() => {
  const buttonsArr = [
    {
      name: t('editPhoto'),
      image: 'image-icon.svg',
      handler: editPhoto,
    },
    {
      name: t('changeName'),
      image: 'pencil-icon.svg',
      handler: () => router.push({ name: 'ChangeName' }),
    },
    {
      name: t('changeLanguage'),
      icon: globeOutline,
      handler: handleChangeLocaleClick,
    },
    {
      name: t('changePassword'),
      icon: lockClosedOutline,
      handler: () => router.push({ name: 'ChangePassword' }),
    },
    {
      name: t('changeEmail'),
      icon: mailOutline,
      handler: () => router.push({ name: 'ChangeEmail' }),
    },
    {
      name: t('logout'),
      image: 'exit-icon.svg',
      handler: () => (state.modalsVisibility.logoutConfirmation = true),
      isHideArrow: true,
    },
    {
      name: t('deleteAccount'),
      image: 'trash-icon.svg',
      handler: () => openDeleteConfirmationModal(),
      isHideArrow: true,
    },
  ] as menuButtonI[];
  if (user.value.isSpecialist) {
    buttonsArr.unshift({
      name: t('aboutMe'),
      icon: personOutline,
      handler: () => router.push({ name: 'AboutMe' }),
    });
  }
  return buttonsArr.filter((b) => {
    if (
      (user.value.authMethod !== 'password' &&
        b.name === t('changePassword')) ||
      (user.value.authMethod !== 'password' && b.name === t('changeEmail'))
    ) {
      return false;
    }
    return true;
  });
});

const backButtonRoutePath = computed(() => {
  return !router.options.history.state.back ? { name: 'MyProfile' } : null;
});

logEvent('view_edit_profile');

function handleChangeLocaleClick() {
  router.push({ name: 'EditProfileChangeLanguage' });
}

async function editPhoto() {
  if (Capacitor.isNativePlatform()) {
    const permissions = await Camera.checkPermissions();
    if (permissions.photos !== 'granted') {
      const { photos } = await Camera.requestPermissions({
        permissions: ['photos'],
      });
      if (photos !== 'granted') {
        return false;
      }
    }
  }

  const image = await Camera.pickImages({
    quality: 60,
    limit: 1,
  });

  await api.users.uploadPhotoByUrl(image.photos[0].webPath);
}

function deletePhoto() {
  state.modalsVisibility.deletePhotoConfirmation = false;
  api.users.deletePhoto();
}

function openDeleteConfirmationModal() {
  state.password = '';
  state.modalsVisibility.deleteAccountConfirmation = true;
}
function closeDeleteConfirmationModal() {
  state.modalsVisibility.deleteAccountConfirmation = false;
}

async function handleDeleteAccount() {
  try {
    state.loadingMap.deleteAccount = true;
    console.log('user.value.authMethod', user.value.authMethod);
    if (user.value.authMethod === 'password') {
      await api.auth.signInWithEmailAndPassword(
        user.value.email,
        state.password
      );
    } else if (user.value.authMethod === 'apple') {
      await api.auth.signInWithApple();
    } else if (user.value.authMethod === 'google') {
      await api.auth.signInWithGoogle();
    }

    await api.auth.deleteMyAccount();
    userStore.unsetUser();
    closeDeleteConfirmationModal();
    router.push({ name: 'Feed' });
    state.loadingMap.deleteAccount = false;
  } catch (e) {
    console.log(e);
    state.loadingMap.deleteAccount = false;
  }
}

async function logout() {
  state.modalsVisibility.logoutConfirmation = false;
  await api.auth.logout();
  await userStore.unsetUser();
  setLocale(userStore.user.language! as SourceLang);
  router.push({ name: 'Feed' });
}
</script>

<style lang="scss" scoped>
.edit-profile-page {
  max-width: 560px;
  padding-bottom: 6em;

  &__avatar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    background: #a9e3ce;
    border-radius: 50%;
    font-size: 40px;
    font-weight: 600;
    line-height: 20px;
    color: #fff;
    margin: 10px auto 0;
    text-transform: uppercase;
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  &__delete-avatar {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border-radius: 50%;
  }

  &__username {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    color: #172659;
    text-align: center;
    margin-top: 16px;
    margin-bottom: 24px;
  }

  &__button {
    width: 100%;
    background-color: #fff;
    border: 1px solid #fff;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    line-height: 18px;
    padding: 15px 17px;
    color: #172659;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    &-arrow {
      display: flex;
    }

    &-icon {
      font-size: 21px;

      ion-icon,
      img {
        width: 24px;
        height: 24px;
      }
    }

    &-left {
      display: flex;
      align-items: center;

      & > *:first-child {
        margin-right: 25px;
      }
    }
  }
}

.modal-container {
  width: 100%;
  height: fit-content;
}

.modal__btn {
  margin-top: 10px;
}

.modal-text {
  margin-bottom: 40px;
  text-align: center;
}

.confirm-modal {
  :deep(ion-title) {
    padding-top: 10px !important;
  }
}

.delete-account-modal {
  :deep(ion-title) {
    color: red;
  }
}
</style>
